import { Group, Material, Mesh, Texture } from 'three/webgpu'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'

/** World-aligned Blender export. No scaling/rotation: the quay footprints match physics. */
export async function loadHarbourModel(): Promise<Group> {
  // Gzip the unchanged GLB; .bin prevents hosts from applying Content-Encoding automatically.
  const response = await fetch(new Request('/models/harbour/harbour.glb.bin'))
  if (!response.ok || !response.body) throw new Error(`Harbour download failed: ${response.status}`)
  const bytes = await new Response(response.body.pipeThrough(new DecompressionStream('gzip'))).arrayBuffer()
  const { scene } = await new GLTFLoader().parseAsync(bytes, '/models/harbour/')
  scene.name = 'harbour-blender'
  const markers = ['quay_terminal', 'quay_fuel', 'quay_west_breakwater', 'quay_east_breakwater', 'quay_east_mole', 'crane_01', 'crane_02']
  if (markers.some(name => !scene.getObjectByName(name))) {
    disposeHarbourModel(scene)
    throw new Error('Harbour model is missing a quay or crane placement marker')
  }
  scene.traverse(object => {
    if (!(object as Mesh).isMesh) return
    object.castShadow = true
    object.receiveShadow = true
  })
  return scene
}

/** Models and fallback docks own their geometry/materials; buoys may have child meshes. */
export function disposeHarbourModel(group: Group): void {
  const geometries = new Set<Mesh['geometry']>()
  const materials = new Set<Material>()
  const textures = new Set<Texture>()
  group.traverse(object => {
    if (!(object as Mesh).isMesh) return
    const mesh = object as Mesh
    geometries.add(mesh.geometry)
    for (const material of Array.isArray(mesh.material) ? mesh.material : [mesh.material]) {
      materials.add(material)
      for (const value of Object.values(material)) if (value instanceof Texture) textures.add(value)
    }
  })
  for (const geometry of geometries) geometry.dispose()
  for (const material of materials) material.dispose()
  for (const texture of textures) texture.dispose()
  group.clear()
}
